import { type Numeric } from "./types";
import isMobile from "ismobilejs";

const toString = Object.prototype.toString;

/**
 *
 * @param {unknow} val 值
 * @param {String} type 类型
 * @returns {Boolean}
 * @example  import { is } from "@ht/utils";
 * is(val, "Object");
 */
export function is(val: unknown, type: string) {
  return toString.call(val) === `[object ${type}]`;
}

/**
 *
 * 是否为函数
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isFunction } from "@ht/utils";
 * isFunction(val);
 */
export function isFunction(val: unknown) {
  return Object.prototype.toString.call(val) === `[object Function]`;
}

/**
 *
 * 值是否存在
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example  import { isDef } from "@ht/utils";
 * isDef(val);
 */
export function isDef<T = unknown>(val?: T): val is T {
  return typeof val !== "undefined";
}

/**
 *
 * 值是否为未定义
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example  import { isUnDef } from "@ht/utils";
 * isUnDef(val);
 */
export function isUnDef<T = unknown>(val?: T): val is T {
  return !isDef(val);
}

/**
 * 是否为对象类型
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isObject } from "@ht/utils";
 * isObject(val);
 */
export function isObject(
  val: unknown
): val is Record<string | number | symbol, unknown> {
  return val !== null && is(val, "Object");
}

/**
 * 是否为日期类型
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isDate } from "@ht/utils";
 * isDate(val);
 */
export const isDate = (val: unknown): val is Date =>
  Object.prototype.toString.call(val) === "[object Date]" &&
  !Number.isNaN((val as Date).getTime());

/**
 * 是否为null
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isNull } from "@ht/utils";
 * isNull(val);
 */
export function isNull(val: unknown): val is null {
  return val === null;
}
/**
 * 是否为数组
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isArray } from "@ht/utils";
 * isArray(val);
 */
export function isArray(val: unknown): val is Array<unknown> {
  return Array.isArray(val);
}
/**
 *
 * 是否为字符串类型
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isString } from "@ht/utils";
 * isString(val);
 */
export function isString(val: unknown): val is string {
  return is(val, "String");
}

/**
 *
 * 是否为布尔值
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isBoolean } from "@ht/utils";
 * isBoolean(val);
 */
export function isBoolean(val: unknown): val is boolean {
  return is(val, "Boolean");
}
/**
 *
 * 是否为正则表达式
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isRegExp } from "@ht/utils";
 * isRegExp(val);
 */
export function isRegExp(val: unknown): val is RegExp {
  return is(val, "RegExp");
}

/**
 *
 * 是否在当前window环境中
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isWindow } from "@ht/utils";
 * isWindow(val);
 */
export function isWindow(val: unknown): val is Window {
  return typeof window !== "undefined" && is(val, "Window");
}
/**
 *
 * 是否为DOM元素
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isElement } from "@ht/utils";
 * isElement(val);
 */
export function isElement(val: unknown): val is Element {
  return isObject(val) && !!val.tagName;
}
/**
 *
 * 是否为Map数据结构
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isMap } from "@ht/utils";
 * isMap(val);
 */
export function isMap(val: unknown): val is Map<unknown, unknown> {
  return is(val, "Map");
}

/**
 *
 * 是否在服务端
 * @returns {Boolean}
 * @example import { isServer } from "@ht/utils";
 * isServer
 */
export const isServer = typeof window === "undefined";

/**
 *  判断是否是浏览器环境
 * @returns {Boolean}
 * @example import { isBrowser } from "@ht/utils";
 * isBrowser
 *
 */
export const isBrowser = typeof window !== "undefined";

/**
 * 判断当前数据类型是否为空，比如对象，数组，Map，Set，字符串等
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example import { isEmpty } from "@ht/utils";
 * isEmpty(val);
 */
export function isEmpty(val: unknown): val is unknown {
  if (isArray(val) || isString(val)) {
    return val.length === 0;
  }

  if (val instanceof Map || val instanceof Set) {
    return val.size === 0;
  }

  if (isObject(val)) {
    return Object.keys(val).length === 0;
  }

  return false;
}

/**
 * 判断是否是数字，包括数字字符串
 * @param {Numeric} val 值
 * @returns {Boolean}
 * @example import { isNumeric } from "@ht/utils";
 * isNumeric(val);
 */
export const isNumeric = (val: Numeric): val is string =>
  typeof val === "number" || /^\d+(\.\d+)?$/.test(val);

/**
 * 判断是否是相同的值
 * @param {unknow} newValue 新值
 * @param {unknow} oldValue 旧值
 * @returns {Boolean}
 * @example import { isSameValue } from "@ht/utils";
 * isSameValue(newValue, oldValue);
 */
export const isSameValue = (newValue: unknown, oldValue: unknown) =>
  JSON.stringify(newValue) === JSON.stringify(oldValue);

/**
 *
 * 判断是否为Promise
 * @param {unknow} val 值
 * @returns {Boolean}
 * @example  * @example import { isPromise } from "@ht/utils";
 * isPromise(val);
 */
export const isPromise = <T = unknown>(val: unknown): val is Promise<T> =>
  isObject(val) && isFunction(val.then) && isFunction(val.catch);

/**
 * 手机号校验
 * @param {number | string} phone 手机号
 * @returns {Boolean}
 * @example import { isMobileNumber } from "@ht/utils";
 * isMobileNumber(phone);
 */
export function isMobileNumber(phone: number | string): boolean {
  return /^(0|86|17951)?(13[0-9]|15[012356789]|166|17[3678]|18[0-9]|14[57])[0-9]{8}$/.test(
    String(phone)
  );
}

/**
 * 是否为IOS手机
 * @returns {Boolean}
 * @example import { isIOSPhone } from "@ht/utils";
 */
export const isIOSPhone = isMobile().apple.device;

/**
 * 是否为安卓手机
 * @returns {Boolean}
 * @example import { isAndroidPhone } from "@ht/utils";
 */
export const isAndroidPhone = isMobile().android.device;
